import axios from "axios";
import {
    getCustomDomainOrg,
    initializeCustomDomain,
} from "../../clients/httpClient";
import {
    IOrganizationStatusResponse,
    ISuspensionNotice,
} from "../../interfaces/suspension-notice.interface";

// Host labels that are never a tenant slug. "staging-app" is the central
// staging host label on digitalawesome.io (tenant staging hosts are
// {tenant}.staging-app.digitalawesome.io, whose first label IS the tenant).
// Exported as the single source of truth — Register.tsx consumes this list
// too, so the two exclusion lists can't drift apart again.
export const NON_TENANT_SUBDOMAINS = ["app", "localhost", "www", "staging-app"];

// The backend resolves the tenant by subdomain, so this call must reach
// {tenant}.<VITE_ORG_API_ENDPOINT> — on the central domain tenancy is never
// initialized and the endpoint always reports not-suspended.
//
// httpClient only rewrites baseURL to the tenant host when localStorage["org"]
// is set, and on a fresh unauthenticated load of {tenant}.app.<domain> it is
// not: initializeCustomDomain only stores an org for hosts present in the
// /custom-domains map, and every other writer runs after login (2FA), on the
// register route, or on the checkout pages. So resolve the slug here — custom
// domains from that map, everything else from the first host label, the same
// derivation used by useImpersonationBoot and Register.
const resolveOrgSlug = async (): Promise<string | null> => {
    await initializeCustomDomain();

    // 1. A custom domain mapped to this exact hostname is authoritative — its
    //    slug bears no relation to the host labels, so it must win.
    const mapped = getCustomDomainOrg();
    if (mapped) return mapped;

    // 2. Otherwise the subdomain actually being visited decides, because that
    //    is how the backend resolves the tenant. It outranks localStorage,
    //    which the checkout and login flows also write and which can therefore
    //    name a different org than the host in front of the user.
    const labels = window.location.hostname.split(".");
    const isTenantHost =
        labels.length > 2 && !NON_TENANT_SUBDOMAINS.includes(labels[0]);

    if (isTenantHost) return labels[0];

    // 3. No tenant in the host (central domain, localhost): fall back to a
    //    stored org so the probe still works where the app is org-scoped by
    //    state rather than by URL.
    return localStorage.getItem("org");
};

// Raw axios, bypassing httpClient: the shared response interceptor toasts and
// Sentry-captures every failure before the caller's catch runs, and this boot
// probe must fail silently. Mirrors fetchProjectAttributesRaw
// (organization-user/organization.service) and getStoreProductSyncStatusRequest
// (store.service). The endpoint is unauthenticated, so no bearer token is sent.
export const getOrganizationStatus = async (): Promise<ISuspensionNotice> => {
    const org = await resolveOrgSlug();
    const protocol = import.meta.env.VITE_BACKEND_PROTOCOL ?? "https";
    const baseURL = org
        ? `${protocol}://${org}.${import.meta.env.VITE_ORG_API_ENDPOINT}`
        : "";

    const response = await axios.get<IOrganizationStatusResponse>(
        `${baseURL}/api/v1/organization/status`,
        {
            headers: {
                Accept: "application/json",
            },
        }
    );
    const body = response.data;

    return {
        isSuspended: Boolean(body?.data?.is_suspended),
        title: body?.data?.title ?? null,
        message: body?.data?.message ?? null,
    };
};
